import type { ModulePermissions } from "@/services/api/user/types";

export interface GuardiansAccessUser {
  company_id?: string;
  role?: string;
  permissions?: string[];
}

/** True for admins, ops module, or Ani company staff (``company_id === "ani"``). */
export function canAccessGuardians(
  hasPermission: (module: string) => boolean,
  user: GuardiansAccessUser | null | undefined,
): boolean {
  if (hasPermission("ops")) {
    return true;
  }
  if ((user?.company_id ?? "").toLowerCase() === "ani") {
    return true;
  }
  if (!user) {
    return false;
  }
  const role = (user.role ?? "").toLowerCase();
  if (role === "admin" || role === "super_admin") {
    return true;
  }
  const perms = user.permissions ?? [];
  return perms.includes("*") || perms.includes("admin:*");
}

/** True when the user may create or edit global Guardians alert rules (Ani company only). */
export function canEditGuardiansRules(
  user: GuardiansAccessUser | null | undefined,
): boolean {
  return (user?.company_id ?? "").toLowerCase() === "ani";
}

/** Pure helper when only module permissions are available (no user profile). */
export function canAccessGuardiansFromPermissions(
  permissions: Pick<ModulePermissions, "permissions" | "modules"> | null | undefined,
  companyId: string | null | undefined,
): boolean {
  if (!permissions) {
    return false;
  }
  const hasPermission = (module: string) => {
    const normalized = module.toLowerCase().replace(/-/g, "_");
    if (
      permissions.permissions.includes("*") ||
      permissions.permissions.includes("admin:*")
    ) {
      return true;
    }
    if (permissions.modules.includes("*") || permissions.modules.includes(normalized)) {
      return true;
    }
    return permissions.modules.some(
      (entry) => entry.split(":")[0]?.toLowerCase().replace(/-/g, "_") === normalized,
    );
  };
  return canAccessGuardians(hasPermission, {
    company_id: companyId ?? undefined,
    permissions: permissions.permissions,
  });
}
